// Rendu PixiJS (WebGL) du jeu : scène retenue mise à jour chaque frame depuis un
// RenderState calculé par le moteur (game.ts). Le moteur ne touche jamais à Pixi :
// cette couche est le seul endroit où brancher effets, filtres et particules.
import {
  Application,
  Container,
  Graphics,
  Rectangle,
  Sprite,
  Text,
  Texture,
  type TextStyleOptions,
} from "pixi.js";
import type { Sprites, SpriteName } from "./assets";
import type { Theme } from "./themes";
import type { UIStrings } from "./i18n";
import { transformPoint } from "./mechanics";
import { customText } from "./custom";

// Résolution logique = résolution native des assets (rendu agrandi en pixel-art).
export const W = 240;
export const H = 160;
const RENDER_SCALE = 2;

// Demi-largeur de la zone de réussite (repères au sol) — partagée avec le moteur.
export const TOLERANCE = 9;

// Pause : 3 options + bouton ⏸ (coin haut-droit) — coordonnées lues aussi par le
// moteur pour le hit-test des taps.
export const PAUSE_OPT_Y = [H / 2 - 4, H / 2 + 12, H / 2 + 28];
export const PAUSE_BTN = { x: W - 10, y: 9, hw: 8, hh: 8 }; // centre + demi-zone tappable

// Textes personnalisés (nez / main) : police pixel, sombre cerné de clair.
const NOSE_TEXT_Y = 44;
const HAND_TEXT_DY = 38;
const LABEL_FONT = '"Pixelify Sans", ui-monospace, monospace';
const LABEL_DARK = "#2b1d00";
const LABEL_LIGHT = "rgba(255, 246, 205, 0.85)";

const MONO = "ui-monospace, Menlo, monospace";

/** Transform courant du nez (translation, rotation, échelle autour du pivot). */
export interface NoseXform {
  dx: number;
  a: number;
  sc: number;
  px: number;
  py: number;
}

/** Tout ce que la scène doit savoir pour dessiner UNE frame. */
export interface RenderState {
  noseFrame: SpriteName;
  nose: NoseXform;
  fingerName: SpriteName;
  fingerTips: number[]; // x des pointes dans le sprite (px natifs)
  fingerTipYInSprite: number;
  fingerScale: number;
  originX: number;
  tipY: number;
  /** true = le doigt passe DERRIÈRE le nez (masqué sauf aux narines). */
  occluded: boolean;
  targets: { x: number; hot: boolean }[];
  hud: {
    score: number;
    misses: number;
    lives: number | null;
    duration: number | null;
    timeLeft: number;
    best: number;
    boostIcon: string | null;
    streakGoal: number | null;
    streak: number;
    combo: number;
    /** true dès que le score courant dépasse le record → score doré. */
    isRecord: boolean;
  };
  /** Traînée d'étincelles au(x) bout(s) du doigt (combo élevé), ou null. */
  trail: { xs: number[]; y: number; max: boolean } | null;
  /** Message central (réussite/échec) ; t = 1 → 0 au fil de l'affichage. */
  msg: { text: string; fail: boolean; t: number; gain: string } | null;
  /** Option surlignée du menu pause, ou null si pas en pause. */
  pauseSel: number | null;
  /** Décompte 3·2·1 restant (0 = pas de décompte). */
  countdown: number;
  over: boolean;
}

/** Texte dont la mise à jour (rasterisation) n'a lieu que si la valeur change. */
function setText(t: Text, s: string): void {
  if (t.text !== s) t.text = s;
}
function setFill(t: Text, color: string): void {
  if (t.style.fill !== color) t.style.fill = color;
}

function makeText(style: TextStyleOptions): Text {
  const t = new Text({ text: "", style: { fontFamily: MONO, fontWeight: "bold", ...style } });
  t.roundPixels = true;
  return t;
}

export class PixiRenderer {
  private readonly tex: Partial<Record<SpriteName, Texture>> = {};

  // Scène (ordre = ordre de dessin du moteur canvas d'origine).
  private noseC!: Container; // corps du nez (transform tangage/zoom)
  private noseSprite!: Sprite;
  private fingerWrap!: Container; // doigt + son occulteur (masque inversé)
  private occluderC!: Container; // porte le masque du nez, même transform que noseC
  private occluderSprite!: Sprite;
  private fingerMaskSprite!: Sprite;
  private fingerSprite!: Sprite;
  private targetG: Graphics[] = [];
  private noseTextC!: Container;
  private noseLabel!: Text;
  private handLabel!: Text;
  // HUD
  private timerFill!: Graphics;
  private timerBg!: Graphics;
  private scoreText!: Text;
  private subText!: Text; // chrono (modes chronométrés) OU vies (modes à vies)
  private missesText!: Text;
  private recordText!: Text;
  private streakText!: Text;
  private comboText!: Text;
  private msgText!: Text;
  private gainText!: Text;
  private pauseBtnG!: Graphics;
  private pauseC!: Container;
  private pauseOpts: Text[] = [];
  private cdC!: Container;
  private cdDisc!: Graphics;
  private cdDigit!: Text;

  private masked = false;
  private lastPauseSel = -1;

  // Particules d'impact : pool de petits carrés teintés, animés par draw().
  private particleC!: Container;
  private particleTex!: Texture;
  private particles: { sp: Sprite; vx: number; vy: number; g: number; life: number; ttl: number }[] = [];
  private particlePool: Sprite[] = [];
  private lastDraw = -1; // horloge interne (ms) pour le dt des particules

  // Coulures de morve : goutte accrochée à la narine (coords LOCALES du nez, pour
  // suivre son tangage) qui gonfle, s'étire puis se détache et tombe.
  private dripG!: Graphics;
  private drips: { lx: number; ly: number; life: number; ttl: number; mult: number; x: number; y: number; vy: number }[] = [];

  // Secousse d'écran (raté) : jitter aléatoire décroissant appliqué à la scène.
  private shakeT = 0;
  private shakeAmp = 0;
  // Flash « RECORD ! » : voile doré bref + texte qui pop puis s'estompe.
  private flashT = 0;
  private flashVeil!: Graphics;
  private flashText!: Text;
  private trailAcc = 0; // accumulateur d'émission de la traînée

  private constructor(
    private readonly app: Application,
    sprites: Sprites,
    private readonly theme: Theme,
    private readonly ui: UIStrings,
  ) {
    // Textures pixel-art (nearest) créées une fois depuis les images déjà chargées.
    for (const [name, img] of Object.entries(sprites)) {
      const t = Texture.from(img);
      t.source.scaleMode = "nearest";
      this.tex[name as SpriteName] = t;
    }
    this.buildScene();
  }

  /** Initialise l'Application Pixi sur le canvas existant puis monte la scène. */
  static async create(
    canvas: HTMLCanvasElement,
    sprites: Sprites,
    theme: Theme,
    ui: UIStrings,
  ): Promise<PixiRenderer> {
    const app = new Application();
    await app.init({
      canvas,
      width: W,
      height: H,
      resolution: RENDER_SCALE, // logique en 240×160, rendu ×2 (pixel-art net)
      autoDensity: false,
      backgroundAlpha: 0, // le fond vient du CSS du canvas (comme avant)
      antialias: false,
      autoStart: false, // la boucle RAF de game-screen pilote le rendu
      sharedTicker: false,
      preference: "webgl",
    });
    return new PixiRenderer(app, sprites, theme, ui);
  }

  /** Sprite de nez (frame ou masque) : recadré et placé selon le thème. */
  private makeNoseSprite(frame: SpriteName): Sprite {
    const { noseSrc: s, noseDst: d } = this.theme;
    const base = this.tex[frame]!;
    const sub = new Texture({ source: base.source, frame: new Rectangle(s.x, s.y, s.w, s.h) });
    const sp = new Sprite(sub);
    sp.position.set(d.x, d.y);
    sp.setSize(d.w, d.h);
    return sp;
  }

  /** Texture (recadrée) d'une frame de nez — mémoïsée par nom via le sprite. */
  private noseTexture(frame: SpriteName): Texture {
    const key = `__nose_${frame}` as SpriteName;
    if (!this.tex[key]) {
      const s = this.theme.noseSrc;
      this.tex[key] = new Texture({
        source: this.tex[frame]!.source,
        frame: new Rectangle(s.x, s.y, s.w, s.h),
      });
    }
    return this.tex[key]!;
  }

  private buildScene(): void {
    const stage = this.app.stage;

    // Sol + repères de narines.
    const ground = new Graphics().rect(0, H - 2, W, 2).fill(0x000000);
    stage.addChild(ground);
    for (let i = 0; i < 2; i++) {
      const g = new Graphics().rect(-TOLERANCE, 0, TOLERANCE * 2, 4).fill(0xffffff);
      g.y = H - 6;
      stage.addChild(g);
      this.targetG.push(g);
    }

    // Nez (le transform tangage/zoom s'applique au conteneur).
    this.noseC = new Container();
    this.noseSprite = this.makeNoseSprite("nez_0");
    this.noseC.addChild(this.noseSprite);
    stage.addChild(this.noseC);


    // Doigt : masque de doigt (contour) + doigt, occultés par le masque du nez
    // (masque alpha INVERSÉ = équivalent du destination-out canvas).
    this.fingerWrap = new Container();
    this.occluderC = new Container();
    this.occluderC.visible = false; // ne s'affiche jamais : ne sert que de masque
    this.occluderSprite = this.makeNoseSprite("nez_0_mask");
    this.occluderC.addChild(this.occluderSprite);
    this.fingerMaskSprite = new Sprite();
    this.fingerSprite = new Sprite();
    this.fingerMaskSprite.roundPixels = true;
    this.fingerSprite.roundPixels = true;
    this.fingerWrap.addChild(this.occluderC, this.fingerMaskSprite, this.fingerSprite);
    stage.addChild(this.fingerWrap);

    // Coulures de morve : PAR-DESSUS le doigt (elle coule sur le doigt planté
    // dans la narine — et reste visible). Redessinées chaque frame.
    this.dripG = new Graphics();
    stage.addChild(this.dripG);

    // Textes personnalisés (nez : suit le transform du nez ; main : suit la pointe).
    const labelStyle = (px: number): TextStyleOptions => ({
      fontFamily: LABEL_FONT,
      fontWeight: "normal",
      fontSize: px,
      fill: LABEL_DARK,
      stroke: { color: LABEL_LIGHT, width: 3, join: "round" },
    });
    this.noseTextC = new Container();
    this.noseLabel = makeText(labelStyle(10));
    this.noseLabel.anchor.set(0.5);
    this.noseTextC.addChild(this.noseLabel);
    this.handLabel = makeText(labelStyle(9));
    this.handLabel.anchor.set(0.5);
    stage.addChild(this.noseTextC, this.handLabel);

    // Particules d'impact : au-dessus du nez/doigt, sous le HUD. Texture = carré
    // blanc 2×2 (teinté par particule) → gros « pixels » cohérents avec le style.
    this.particleC = new Container();
    stage.addChild(this.particleC);
    const dot = new Graphics().rect(0, 0, 2, 2).fill(0xffffff);
    this.particleTex = this.app.renderer.generateTexture({ target: dot, resolution: RENDER_SCALE });
    this.particleTex.source.scaleMode = "nearest";
    dot.destroy();

    // ---- HUD ----
    this.timerBg = new Graphics().rect(0, 0, W, 3).fill({ color: 0x000000, alpha: 0.12 });
    this.timerFill = new Graphics().rect(0, 0, W, 3).fill(0xffffff);
    stage.addChild(this.timerBg, this.timerFill);

    this.scoreText = makeText({ fontSize: 11, fill: "#000" });
    this.scoreText.position.set(4, 6);
    this.subText = makeText({ fontSize: 15, fill: "#000" });
    this.subText.position.set(4, 19);
    this.missesText = makeText({ fontSize: 11, fill: "#000" });
    this.missesText.anchor.set(1, 0);
    this.missesText.position.set(W - 19, 6);
    this.recordText = makeText({ fontSize: 11, fill: "#000" });
    this.recordText.anchor.set(1, 0);
    this.recordText.position.set(W - 19, 18);
    this.streakText = makeText({ fontSize: 13, fill: "#000" });
    this.streakText.anchor.set(0.5, 0);
    this.streakText.position.set(W / 2, 6);
    this.comboText = makeText({ fontSize: 13, fill: "#e67e22" });
    this.comboText.anchor.set(0.5, 0);
    this.comboText.position.set(W / 2, 19);
    stage.addChild(this.scoreText, this.subText, this.missesText, this.recordText, this.streakText, this.comboText);

    // Message central (réussite/échec) + détail du gain.
    this.msgText = makeText({
      fontSize: 13,
      fill: "#14963c",
      stroke: { color: "#ffffff", width: 3, join: "round" },
    });
    this.msgText.anchor.set(0.5);
    this.gainText = makeText({
      fontSize: 9,
      fill: "#147832",
      stroke: { color: "#ffffff", width: 3, join: "round" },
    });
    this.gainText.anchor.set(0.5);
    stage.addChild(this.msgText, this.gainText);

    // Flash « RECORD ! » : voile doré + texte (au-dessus du jeu, sous la pause).
    this.flashVeil = new Graphics().rect(0, 0, W, H).fill(0xffd23f);
    this.flashVeil.visible = false;
    this.flashText = makeText({
      fontSize: 15,
      fill: "#ffd23f",
      stroke: { color: "#7a5c00", width: 3, join: "round" },
    });
    this.flashText.text = "🏆 RECORD !";
    this.flashText.anchor.set(0.5);
    this.flashText.position.set(W / 2, 52);
    this.flashText.visible = false;
    stage.addChild(this.flashVeil, this.flashText);

    // Bouton ⏸ (coin haut-droit).
    const { x: bx, y: by } = PAUSE_BTN;
    this.pauseBtnG = new Graphics()
      .rect(bx - 3, by - 4, 2, 8)
      .rect(bx + 1, by - 4, 2, 8)
      .fill({ color: 0x000000, alpha: 0.45 });
    stage.addChild(this.pauseBtnG);

    // Overlay PAUSE : voile + titre + 3 options.
    this.pauseC = new Container();
    const veil = new Graphics().rect(0, 0, W, H).fill({ color: 0x000000, alpha: 0.55 });
    const title = makeText({ fontSize: 18, fill: "#fff" });
    title.text = "PAUSE";
    title.anchor.set(0.5);
    title.position.set(W / 2, H / 2 - 28);
    this.pauseC.addChild(veil, title);
    const opts = [this.ui.resume, this.ui.restart, this.ui.quitMenu];
    for (let i = 0; i < 3; i++) {
      const t = makeText({ fontSize: 12, fill: "#9aa7b4" });
      t.text = `  ${opts[i]!}`;
      t.anchor.set(0.5);
      t.position.set(W / 2, PAUSE_OPT_Y[i]!);
      this.pauseC.addChild(t);
      this.pauseOpts.push(t);
    }
    stage.addChild(this.pauseC);

    // Décompte 3·2·1 : voile + disque + anneau doré + chiffre.
    this.cdC = new Container();
    const cdVeil = new Graphics().rect(0, 0, W, H).fill({ color: 0x000000, alpha: 0.4 });
    this.cdDisc = new Graphics();
    this.cdDigit = makeText({ fontSize: 30, fill: "#fff" });
    this.cdDigit.anchor.set(0.5);
    this.cdDigit.position.set(W / 2, H / 2 + 1);
    this.cdC.addChild(cdVeil, this.cdDisc, this.cdDigit);
    stage.addChild(this.cdC);
  }

  /** Applique le transform du nez (pivot haut-milieu) à un conteneur. */
  private static applyNoseXform(c: Container, n: NoseXform): void {
    c.pivot.set(n.px, n.py);
    c.position.set(n.px + n.dx, n.py);
    c.rotation = n.a;
    c.scale.set(n.sc);
  }

  /** Secousse d'écran (raté) : ~0,3 s de jitter décroissant. */
  shake(mult = 1): void {
    this.shakeT = 0.3;
    this.shakeAmp = 3.5 * mult;
  }

  /** Flash « RECORD ! » : à déclencher quand le score dépasse le record en cours
   *  de partie (voile doré bref + texte qui pop). */
  recordFlash(): void {
    this.flashT = 1.1;
  }

  /** Jaillissement de particules à l'impact (coordonnées logiques).
   *  booger = réussite (morve verte), perfect = PARFAIT (or + vert),
   *  dust = raté (nuage de poussière sur le nez). */
  burst(x: number, y: number, kind: "booger" | "perfect" | "dust"): void {
    const defs = {
      booger: { n: 12, colors: [0x6ab04c, 0x8bc34a, 0x3f6d2a], speed: 55, up: -42, g: 170, ttl: 0.65 },
      perfect: { n: 18, colors: [0xffd23f, 0xffe98a, 0x6ab04c, 0x8bc34a], speed: 70, up: -55, g: 160, ttl: 0.8 },
      dust: { n: 9, colors: [0xd8d0c8, 0xbdb4aa, 0xf2ece4], speed: 45, up: -18, g: 90, ttl: 0.45 },
    }[kind];
    for (let i = 0; i < defs.n; i++) {
      const sp = this.particlePool.pop() ?? new Sprite(this.particleTex);
      sp.anchor.set(0.5);
      sp.tint = defs.colors[i % defs.colors.length]!;
      sp.alpha = 1;
      sp.scale.set(Math.random() < 0.4 ? 2 : 1); // quelques gros morceaux
      sp.position.set(x, y);
      this.particleC.addChild(sp);
      const a = Math.random() * Math.PI * 2;
      const spd = defs.speed * (0.35 + Math.random() * 0.65);
      this.particles.push({
        sp,
        vx: Math.cos(a) * spd,
        vy: Math.sin(a) * spd * 0.6 + defs.up,
        g: defs.g,
        life: 0,
        ttl: defs.ttl * (0.7 + Math.random() * 0.5),
      });
    }
  }

  /** Coulure de morve depuis une narine — `lx, ly` en coordonnées LOCALES du nez
   *  (elle reste accrochée même quand le nez tangue). `mult` grossit la goutte. */
  drip(lx: number, ly: number, mult = 1): void {
    if (this.drips.length >= 6) this.drips.shift(); // garde-fou
    this.drips.push({ lx, ly, life: 0, ttl: 1.8, mult, x: 0, y: 0, vy: 14 });
  }

  /** Anime et dessine les coulures (Graphics redessiné : 3-4 formes par goutte). */
  private drawDrips(dt: number, nose: NoseXform, frozen: boolean): void {
    const DETACH = 0.42; // fraction de vie passée accrochée à la narine
    const g = this.dripG.clear();
    for (let i = this.drips.length - 1; i >= 0; i--) {
      const d = this.drips[i]!;
      if (!frozen) d.life += dt;
      if (d.life >= d.ttl) {
        this.drips.splice(i, 1);
        continue;
      }
      const t = d.life / d.ttl;
      const a = transformPoint(d.lx, d.ly + 1, nose); // accroche : bord bas de la narine
      const fade = t > 0.75 ? 1 - (t - 0.75) / 0.25 : 1;
      // Résidu qui reste sur la narine puis sèche.
      g.circle(a.x, a.y, 1.3 * d.mult).fill({ color: 0x4d8637, alpha: 0.9 * fade });
      if (t < DETACH) {
        // Accrochée : la goutte gonfle et s'affaisse, filament qui s'amincit.
        const k = t / DETACH;
        const dropY = a.y + 2 + k * k * 10;
        const r = (1.8 + 2.4 * k) * d.mult;
        const w = 3.2 * (1 - 0.65 * k) * d.mult;
        g.moveTo(a.x - w / 2, a.y)
          .lineTo(a.x + w / 2, a.y)
          .lineTo(a.x + w * 0.18, dropY)
          .lineTo(a.x - w * 0.18, dropY)
          .closePath()
          .fill(0x6ab04c);
        g.circle(a.x, dropY, r).fill(0x6ab04c);
        g.circle(a.x - r * 0.35, dropY - r * 0.35, r * 0.3).fill({ color: 0xa8d98a, alpha: 0.9 }); // reflet
        d.x = a.x;
        d.y = dropY;
      } else {
        // Détachée : chute libre, goutte étirée par la vitesse, fondu en fin de vie.
        if (!frozen) {
          d.vy += 240 * dt;
          d.y += d.vy * dt;
        }
        if (d.y > H + 6) {
          this.drips.splice(i, 1);
          continue;
        }
        const r = 4.2 * d.mult;
        const stretch = Math.min(2.2, 1 + d.vy / 120);
        g.ellipse(d.x, d.y, r * 0.72, r * 0.72 * stretch).fill({ color: 0x6ab04c, alpha: fade });
        g.circle(d.x - r * 0.25, d.y - r * 0.4 * stretch, r * 0.25).fill({ color: 0xa8d98a, alpha: 0.8 * fade });
      }
    }
  }

  /** Secousse, flash record et traînée du doigt — animés au fil des frames. */
  private updateEffects(dt: number, s: RenderState, frozen: boolean): void {
    // Secousse : jitter aléatoire qui décroît, appliqué à toute la scène.
    if (!frozen) this.shakeT = Math.max(0, this.shakeT - dt);
    const k = this.shakeT / 0.3;
    if (k > 0) {
      const a = this.shakeAmp * k;
      this.app.stage.position.set((Math.random() * 2 - 1) * a, (Math.random() * 2 - 1) * a * 0.7);
    } else {
      this.app.stage.position.set(0, 0);
    }

    // Flash record : voile doré bref (0,25 s) puis texte qui pop et s'estompe.
    if (!frozen) this.flashT = Math.max(0, this.flashT - dt);
    const f = this.flashT;
    this.flashVeil.visible = f > 0.85;
    if (this.flashVeil.visible) this.flashVeil.alpha = ((f - 0.85) / 0.25) * 0.35;
    this.flashText.visible = f > 0 && !s.over;
    if (this.flashText.visible) {
      const pop = Math.max(0, f - 0.9) / 0.2; // 1 → 0 sur les 0,2 premières secondes
      this.flashText.scale.set(1 + 0.5 * pop);
      this.flashText.alpha = Math.min(1, f / 0.35);
    }

    // Traînée du doigt (combo élevé) : étincelles émises au(x) bout(s) du doigt.
    if (s.trail && !frozen && !s.over && s.countdown <= 0) {
      this.trailAcc += dt;
      const period = 1 / 40; // ~40 étincelles/s et par pointe
      while (this.trailAcc >= period) {
        this.trailAcc -= period;
        for (const x of s.trail.xs) {
          const sp = this.particlePool.pop() ?? new Sprite(this.particleTex);
          sp.anchor.set(0.5);
          sp.tint = s.trail.max ? 0xffd23f : 0xe67e22;
          sp.alpha = 0.9;
          sp.scale.set(1);
          sp.position.set(x + (Math.random() * 4 - 2), s.trail.y + Math.random() * 5);
          this.particleC.addChild(sp);
          this.particles.push({
            sp,
            vx: (Math.random() - 0.5) * 12,
            vy: -(8 + Math.random() * 14),
            g: 0,
            life: 0,
            ttl: 0.22 + Math.random() * 0.14,
          });
        }
      }
    } else {
      this.trailAcc = 0;
    }
  }

  /** Avance les particules (gravité + fondu), recycle les mortes dans le pool. */
  private updateParticles(dt: number): void {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i]!;
      p.life += dt;
      if (p.life >= p.ttl) {
        this.particleC.removeChild(p.sp);
        this.particlePool.push(p.sp);
        this.particles.splice(i, 1);
        continue;
      }
      p.vy += p.g * dt;
      p.sp.x += p.vx * dt;
      p.sp.y += p.vy * dt;
      p.sp.alpha = 1 - (p.life / p.ttl) ** 2;
    }
  }

  /** Dessine la frame courante : met à jour la scène puis rend.
   *  `dtOverride` (tests) remplace l'horloge interne des particules. */
  draw(s: RenderState, dtOverride?: number): void {
    // Particules : dt réel clampé (indépendant du moteur), figées en pause.
    const now = performance.now();
    const dt = dtOverride ?? (this.lastDraw < 0 ? 0 : Math.min(0.05, (now - this.lastDraw) / 1000));
    this.lastDraw = now;
    const frozen = s.pauseSel !== null;
    if (!frozen) this.updateParticles(dt);
    this.drawDrips(dt, s.nose, frozen);
    this.updateEffects(dt, s, frozen);

    // ---- Nez ----
    const noseTex = this.noseTexture(s.noseFrame);
    if (this.noseSprite.texture !== noseTex) this.noseSprite.texture = noseTex;
    PixiRenderer.applyNoseXform(this.noseC, s.nose);

    // ---- Doigt + occlusion ----
    const fMask = this.tex[`${s.fingerName}_mask` as SpriteName]!;
    const fTex = this.tex[s.fingerName]!;
    if (this.fingerSprite.texture !== fTex) {
      this.fingerSprite.texture = fTex;
      this.fingerMaskSprite.texture = fMask;
      this.fingerSprite.scale.set(s.fingerScale);
      this.fingerMaskSprite.scale.set(s.fingerScale);
    }
    const fx = Math.round(s.originX);
    const fy = Math.round(s.tipY - s.fingerTipYInSprite * s.fingerScale);
    this.fingerSprite.position.set(fx, fy);
    this.fingerMaskSprite.position.set(fx, fy);

    const occTex = this.noseTexture(`${s.noseFrame}_mask` as SpriteName);
    if (this.occluderSprite.texture !== occTex) this.occluderSprite.texture = occTex;
    PixiRenderer.applyNoseXform(this.occluderC, s.nose);
    if (s.occluded !== this.masked) {
      this.masked = s.occluded;
      if (s.occluded) {
        this.occluderC.visible = true;
        this.fingerWrap.setMask({ mask: this.occluderSprite, inverse: true });
      } else {
        this.fingerWrap.mask = null;
        this.occluderC.visible = false; // sinon le masque s'afficherait comme sprite
      }
    }

    // ---- Repères au sol ----
    for (let i = 0; i < 2; i++) {
      const g = this.targetG[i]!;
      const t = s.targets[i];
      g.visible = !!t;
      if (!t) continue;
      g.x = t.x;
      g.tint = t.hot ? 0x2ecc71 : 0x000000;
      g.alpha = t.hot ? 1 : 0.18;
    }

    // ---- Textes personnalisés ----
    setText(this.noseLabel, customText.nose ?? "");
    this.noseTextC.visible = !!customText.nose;
    if (customText.nose) {
      PixiRenderer.applyNoseXform(this.noseTextC, s.nose);
      this.noseLabel.position.set(s.nose.px, NOSE_TEXT_Y);
    }
    setText(this.handLabel, customText.hand ?? "");
    this.handLabel.visible = !!customText.hand;
    if (customText.hand) {
      const tips = s.fingerTips;
      const cx = s.originX + ((tips[0]! + tips[tips.length - 1]!) / 2) * s.fingerScale;
      this.handLabel.position.set(cx, s.tipY + HAND_TEXT_DY);
    }

    this.drawHud(s);

    this.pauseBtnG.visible = !s.over && s.pauseSel === null;
    this.drawPause(s.pauseSel);
    this.drawCountdown(s);

    this.app.render();
  }

  private drawHud(s: RenderState): void {
    const h = s.hud;
    const dur = h.duration;
    const frac = dur !== null ? Math.max(0, h.timeLeft) / dur : 0;

    // Barre de temps dégressive (modes chronométrés).
    this.timerBg.visible = dur !== null;
    this.timerFill.visible = dur !== null;
    if (dur !== null) {
      this.timerFill.scale.x = frac;
      this.timerFill.tint = frac > 0.5 ? 0x2ecc71 : frac > 0.25 ? 0xf1c40f : 0xe74c3c;
    }

    setText(this.scoreText, `${this.ui.score} ${h.score}${h.boostIcon ? ` ${h.boostIcon}` : ""}`);
    setFill(this.scoreText, h.isRecord ? "#c9a227" : "#000"); // doré au-delà du record

    // Sous le score : CHRONO (couleur selon l'urgence) ou VIES.
    if (dur !== null) {
      setText(this.subText, `⏱${Math.ceil(Math.max(0, h.timeLeft))}s`);
      setFill(this.subText, frac > 0.5 ? "#1e8449" : frac > 0.25 ? "#b9770e" : "#c0392b");
      if (this.subText.style.fontSize !== 15) this.subText.style.fontSize = 15;
      this.subText.y = 19;
      this.subText.visible = true;
    } else if (h.lives !== null) {
      const left = Math.max(0, h.lives - h.misses);
      setText(this.subText, "❤".repeat(left) || "—");
      setFill(this.subText, "#c0392b");
      if (this.subText.style.fontSize !== 11) this.subText.style.fontSize = 11;
      this.subText.y = 18;
      this.subText.visible = true;
    } else {
      this.subText.visible = false;
    }

    const lim = h.lives !== null ? `/${h.lives}` : "";
    setText(this.missesText, `${this.ui.misses} ${h.misses}${lim}`);
    setText(this.recordText, `${this.ui.best} ${h.best}`);

    this.streakText.visible = h.streakGoal !== null;
    if (h.streakGoal !== null) setText(this.streakText, `${h.streak}/${h.streakGoal}`);

    this.comboText.visible = h.combo >= 2;
    if (h.combo >= 2) {
      const sz = Math.min(17, 10 + h.combo);
      if (this.comboText.style.fontSize !== sz) this.comboText.style.fontSize = sz;
      setText(this.comboText, `COMBO ×${h.combo}`);
    }

    // Message central : petit « pop » vers le bas à l'apparition, puis fondu.
    const m = s.msg;
    this.msgText.visible = !!m;
    this.gainText.visible = !!m && !m.fail && !!m.gain;
    if (m) {
      const y = 95 - (1 - Math.min(1, m.t * 3)) * 6;
      setText(this.msgText, m.text);
      setFill(this.msgText, m.fail ? "#d22828" : "#14963c");
      this.msgText.position.set(W / 2, y);
      this.msgText.alpha = m.t;
      if (this.gainText.visible) {
        setText(this.gainText, m.gain);
        this.gainText.position.set(W / 2, y + 13);
        this.gainText.alpha = m.t;
      }
    }
  }

  private drawPause(sel: number | null): void {
    this.pauseC.visible = sel !== null;
    if (sel === null || sel === this.lastPauseSel) {
      if (sel === null) this.lastPauseSel = -1;
      return;
    }
    this.lastPauseSel = sel;
    const labels = [this.ui.resume, this.ui.restart, this.ui.quitMenu];
    this.pauseOpts.forEach((t, i) => {
      setText(t, `${i === sel ? "▶ " : "  "}${labels[i]!}`);
      setFill(t, i === sel ? "#ffe14d" : "#9aa7b4");
    });
  }

  private drawCountdown(s: RenderState): void {
    const show = s.countdown > 0 && !s.over && s.pauseSel === null;
    this.cdC.visible = show;
    if (!show) return;
    const n = Math.ceil(s.countdown);
    const p = n - s.countdown; // 0 → 1 au fil de la seconde
    const r = 27 * (1.12 - 0.12 * p); // léger « pop » à chaque seconde
    // Anneau doré qui se vide : arc depuis midi (moveTo explicite, sinon le
    // tracé relierait l'origine du path au départ de l'arc).
    const a0 = -Math.PI / 2;
    const a1 = a0 + (1 - p) * Math.PI * 2;
    this.cdDisc
      .clear()
      .circle(W / 2, H / 2, r)
      .fill(0x15202b)
      .moveTo(W / 2 + (r - 2) * Math.cos(a0), H / 2 + (r - 2) * Math.sin(a0))
      .arc(W / 2, H / 2, r - 2, a0, a1)
      .stroke({ width: 4, color: 0xffd23f, cap: "round" });
    setText(this.cdDigit, String(n));
  }

  /** Largeur native (px source) d'un sprite — lue par la logique de défilement. */
  textureWidth(name: SpriteName): number {
    return this.tex[name]?.width ?? 0;
  }

  /** Libère le GPU — appelé au retour menu. Détruit TOUTES les textures du
   *  thème (pas seulement celles attachées à la scène : frames de réaction et
   *  doigts non affichés compris), sinon elles fuient à chaque partie. */
  destroy(): void {
    for (const [name, t] of Object.entries(this.tex)) {
      t.destroy(!name.startsWith("__nose_")); // sous-textures : source partagée
    }
    for (const sp of this.particlePool) sp.destroy(); // sprites recyclés hors scène
    this.particleTex.destroy(true);
    this.app.destroy({ removeView: false, releaseGlobalResources: true }, { children: true });
  }
}
